<template>
  <div class="home-container">
    <!-- 全屏视差滚动英雄区 -->
    <div class="hero-section" ref="heroSection">
      <!-- 粒子背景 -->
      <div id="particles-js" class="particles-container"></div>
      
      <div class="hero-content" :style="{ transform: `translateY(${scrollY * 0.5}px)` }">
        <div class="title-wrapper">
          <div class="title-line"></div>
          <h1 class="animate__animated animate__fadeInUp">
            <span class="letter-animation" v-for="(letter, index) in '行序科技'" :key="index" 
                  :style="{ animationDelay: `${index * 0.1}s` }">
              {{ letter }}
            </span>
          </h1>
          <div class="title-line"></div>
        </div>
        <p class="animate__animated animate__fadeInUp animate__delay-1s">
          创新技术 引领未来
        </p>
        <el-button 
          type="primary" 
          size="large" 
          class="animate__animated animate__fadeInUp animate__delay-2s"
          @click="scrollToProducts"
        >
          了解更多
        </el-button>
      </div>
      <div class="hero-overlay"></div>
    </div>

    <!-- 轮播图区域 -->
    <div class="carousel-section">
      <el-carousel 
        :interval="4000" 
        type="card"
        :height="isMobile ? '250px' : '500px'"
      >
        <el-carousel-item v-for="item in carouselItems" :key="item.id">
          <div class="carousel-content" :style="{ backgroundImage: `url(${item.image})` }">
            <div class="carousel-text">
              <h2>{{ item.title }}</h2>
              <p>{{ item.description }}</p>
            </div>
          </div>
        </el-carousel-item>
      </el-carousel>
    </div>

    <!-- 产品展示区 -->
    <div class="products-section" ref="productsSection">
      <div class="section-title">
        <h2>产品展示</h2>
        <div class="title-line"></div>
      </div>
      <!-- 产品网格 -->
      <div class="products-grid" :class="{ 'collapsed': !showAllProducts }">
        <div 
          v-for="product in displayedProducts" 
          :key="product.id" 
          class="product-card"
          @click="navigateToProduct(product)"
        >
          <div class="product-image">
            <img :src="product.image" :alt="product.name">
          </div>
          <div class="product-info">
            <h3>{{ product.name }}</h3>
            <p>{{ product.description }}</p>
          </div>
        </div>
      </div>
      
      <!-- 收起展开按钮 -->
      <div class="products-toggle" v-if="needsToggleButton">
        <!-- 收起状态下的提示 -->
        <div v-if="!showAllProducts" class="products-hint">
          <p>还有 {{ products.length - initialDisplayCount }} 个产品等待发现</p>
        </div>
        
        <el-button 
          type="primary" 
          @click="toggleProducts"
          class="toggle-btn"
        >
          <el-icon v-if="showAllProducts" class="icon-up"><ArrowUp /></el-icon>
          <el-icon v-else class="icon-down"><ArrowDown /></el-icon>
          {{ showAllProducts ? '收起产品' : '展开更多产品' }}
        </el-button>
      </div>
    </div>

    <!-- 公司优势 -->
    <div class="advantages-section">
      <div class="section-title">
        <h2>我们的优势</h2>
        <div class="title-line"></div>
      </div>
      <div class="advantages-grid">
        <div 
          v-for="(advantage, index) in advantages" 
          :key="index"
          class="advantage-card"
          :class="{ 'animate__animated animate__fadeInUp': isVisible }"
          :style="{ 'animation-delay': `${index * 0.2}s` }"
        >
          <i :class="advantage.icon"></i>
          <h3>{{ advantage.title }}</h3>
          <p>{{ advantage.description }}</p>
        </div>
      </div>
    </div>

    <!-- 数据统计 -->
    <div class="stats-section" ref="statsSection">
      <div class="stats-grid">
        <div 
          v-for="(stat, index) in stats" 
          :key="index" 
          class="stat-card"
        >
          <div class="stat-number" v-if="isStatsVisible">
            <count-to 
              :start-val="0"
              :end-val="stat.number"
              :duration="2500"
              :autoplay="true"
            ></count-to>
            <span>{{ stat.unit }}</span>
          </div>
          <div class="stat-label">{{ stat.label }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, computed, markRaw } from 'vue'
import { useRouter } from 'vue-router'
import CountTo from 'vue-count-to'
import { ArrowUp, ArrowDown } from '@element-plus/icons-vue'
import { fetchPublishedBanners, fetchPublishedProducts, mergeProducts } from '@/utils/cms'

export default {
  name: 'NewHome',
  components: {
    CountTo,
    ArrowUp: markRaw(ArrowUp),
    ArrowDown: markRaw(ArrowDown)
  },
  setup() {
    const router = useRouter()
    const scrollY = ref(0)
    const isVisible = ref(false)
    const isStatsVisible = ref(false)
    const heroSection = ref(null)
    const statsSection = ref(null)
    const showContent = ref(false)
    const companyName = '北京行序科技有限公司'
    const showAllProducts = ref(false)

    // 判断是否为移动端
    const isMobile = computed(() => {
      if (typeof window !== 'undefined') { // 确保在浏览器环境中执行
        return window.innerWidth <= 768
      }
      return false // 或者根据需要返回默认值
    })

    // 根据设备类型设置初始显示数量
    const initialDisplayCount = computed(() => {
      return isMobile.value ? 6 : 8 // 移动端显示6个，桌面端显示8个
    })

    const fallbackCarouselItems = [
      {
        id: 1,
        image: '/images/轮播图片1--X射线毛细管光学.png',
        title: 'X射线毛细管光学',
        description: '高精度X射线光学元件，实现精确的光束控制'
      },
      {
        id: 2,
        image: '/images/轮播图片2-聚焦准直x射线多毛细管.png',
        title: '聚焦准直X射线多毛细管',
        description: '先进的多毛细管技术，提供优异的聚焦和准直性能'
      },
      {
        id: 3,
        image: '/images/轮播图片3-X射线导光板.png',
        title: 'X射线导光板',
        description: '高效的X射线传输和准直解决方案'
      }
    ]

    const fallbackProducts = [
      {
        id: 1,
        name: '聚焦多毛细管光学元件',
        description: '聚焦 X 射线可实现小焦点和高强度。',
        image: '/images/product1.png'
      },
      {
        id: 2,
        name: '准直多毛细光学元件',
        description: '从发散输入光束到准平行输出光束。',
        image: '/images/product2.png'
      },
      {
        id: 3,
        name: 'X 射线导光板',
        description: '有效传输和准直发散的 X 射线。',
        image: '/images/product3.png'
      },
      {
        id: 4,
        name: '成像光学元件',
        description: '用于 X 射线光束整形的高精度定制镜组。',
        image: '/images/product4.png'
      },
      {
        id: 5,
        name: 'AXUV',
        description: '高性能光子、电子或X射线测量。',
        image: '/images/AXUV.png'
      },
      {
        id: 6,
        name: 'SXUV',
        description: 'EUV优化光电二极管，适用于13.5nm光刻。',
        image: '/images/SXUV.png' 
      },
      {
        id: 7,
        name: 'UVG',
        description: '可靠的紫外探测器，用于UV-A/B/C监测。',
        image: '/images/UVG.png' 
      },
      {
        id: 8, 
        name: 'NIRONE Sensor S',
        description: '小巧便携的近红外光谱传感器。',
        image: '/images/SensorS.png'
      },
      // 新增UV位置传感器产品
      {
        id: 11,
        name: 'S1-0073-1L10UV_CP2',
        description: '一维UV位置传感器，10×2mm²有效区域。',
        image: '/images/S1-0073-1L10UV_CP2_1.png'
      },
      {
        id: 12,
        name: 'S1-0074-1L20UV_CP3',
        description: '一维UV位置传感器，20×3mm²有效区域。',
        image: '/images/S1-0074-1L20UV_CP3_2.png'
      },
      {
        id: 13,
        name: 'S2-0184-2L4_SU71',
        description: '二维UV位置传感器，4×4mm²有效区域。',
        image: '/images/S2-0184-2L4_SU71_1.png'
      },
      {
        id: 14,
        name: 'S2-0034-2L20UV_SU9',
        description: '二维UV位置传感器，20×20mm²有效区域。',
        image: '/images/S2-0034-2L20UV_SU9_1.png'
      },
      {
        id: 15,
        name: 'S2-0030-2L2UV_MP1',
        description: '二维UV位置传感器，2×2mm²有效区域。',
        image: '/images/S2-0030-2L2UV_MP1_1.png'
      },
      {
        id: 16,
        name: 'S2-0016_2L10UV_SU7',
        description: '二维UV位置传感器，10×10mm²有效区域。',
        image: '/images/S2-0016_2L10UV_SU7_1.png'
      },
      {
        id: 17,
        name: 'S2-0006-2L4UV_MP1',
        description: '二维UV位置传感器，4×4mm²有效区域。',
        image: '/images/S2-0006-2L4UV_MP1_1.png'
      },
      {
        id: 18,
        name: 'S1-0356-D_A',
        description: 'PSD带外壳UV探测器，10×2mm²有效区域。',
        image: '/images/S1-0356-K_A.png'
      },
      {
        id: 19,
        name: 'S1-0356-K_A',
        description: 'PSD带外壳UV探测器，10×2mm²有效区域。',
        image: '/images/S1-0356-K_A.png'
      },
      {
        id: 20,
        name: 'S2-0385-D',
        description: 'PSD带外壳UV探测器，4×4mm²有效区域。',
        image: '/images/S2-0385-K.png'
      },
      {
        id: 21,
        name: 'S2-0385-K',
        description: 'PSD带外壳UV探测器，4×4mm²有效区域。',
        image: '/images/S2-0385-K.png'
      },
      {
        id: 22,
        name: 'PSD Holder MH01',
        description: 'PSD带外壳UV探测器，专业光学封装。',
        image: '/images/PSD Holder MH01 data sheet.png'
      },
      {
        id: 23,
        name: 'SE-0018 SEEPOS',
        description: 'SE-0018系列产品，专业光学解决方案。',
        image: '/images/SE-0018 SEEPOS.png'
      },
      {
        id: 24,
        name: 'SE-0011-D_D',
        description: '二位电路放大器，信号处理电路。',
        image: '/images/SE-0011-D_D_1.png'
      }
    ]

    const carouselItems = ref(fallbackCarouselItems)
    const products = ref(fallbackProducts)

    const displayedProducts = computed(() => {
      if (showAllProducts.value) {
        return products.value
      }
      return products.value.slice(0, initialDisplayCount.value)
    })

    const toggleProducts = () => {
      showAllProducts.value = !showAllProducts.value
      if (showAllProducts.value) {
        setTimeout(() => {
          const productsSection = document.querySelector('.products-section')
          if (productsSection) {
            productsSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        }, 100)
      }
    }

    const needsToggleButton = computed(() => {
      return products.value.length > initialDisplayCount.value
    })

    const advantages = [
      {
        icon: 'el-icon-cpu',
        title: '技术创新',
        description: '持续的技术创新和研发投入'
      },
      // ... 更多优势
    ]

    const stats = [
      {
        number: 100,
        unit: '+',
        label: '服务客户'
      },
      // ... 更多统计数据
    ]

    const handleScroll = () => {
      scrollY.value = window.scrollY
      checkVisibility()
    }

    const checkVisibility = () => {
      if (statsSection.value) {
        const rect = statsSection.value.getBoundingClientRect()
        isStatsVisible.value = rect.top < window.innerHeight && rect.bottom >= 0
      }
    }

    const scrollToProducts = () => {
      const productsSection = document.querySelector('.products-section')
      if (productsSection) {
        productsSection.scrollIntoView({ behavior: 'smooth' })
      }
    }

    const navigateToProduct = (product) => {
      router.push({
        name: 'ProductDetail',
        params: { id: product.id.toString() }
      })
    }

    const initParticles = () => {
      if (window.particlesJS) {
        window.particlesJS('particles-js', {
          particles: {
            number: {
              value: 80,
              density: {
                enable: true,
                value_area: 800
              }
            },
            color: {
              value: '#ffffff'
            },
            shape: {
              type: 'circle'
            },
            opacity: {
              value: 0.5,
              random: false,
              animation: {
                enable: true,
                speed: 1,
                opacity_min: 0.1,
                sync: false
              }
            },
            size: {
              value: 3,
              random: true,
              animation: {
                enable: true,
                speed: 2,
                size_min: 0.1,
                sync: false
              }
            },
            line_linked: {
              enable: true,
              distance: 150,
              color: '#ffffff',
              opacity: 0.4,
              width: 1
            },
            move: {
              enable: true,
              speed: 2,
              direction: 'none',
              random: false,
              straight: false,
              out_mode: 'out',
              bounce: false,
            }
          },
          interactivity: {
            detect_on: 'canvas',
            events: {
              onhover: {
                enable: true,
                mode: 'grab'
              },
              onclick: {
                enable: true,
                mode: 'push'
              },
              resize: true
            },
            modes: {
              grab: {
                distance: 140,
                line_linked: {
                  opacity: 1
                }
              },
              push: {
                particles_nb: 4
              }
            }
          },
          retina_detect: true
        })
      }
    }

    onMounted(async () => {
      window.addEventListener('scroll', handleScroll)
      checkVisibility()

      // 动态加载 particles.js
      const script = document.createElement('script')
      script.src = 'https://cdn.jsdelivr.net/particles.js/2.0.0/particles.min.js'
      script.onload = () => {
        initParticles()
      }
      document.head.appendChild(script)

      // 显示内容动画
      setTimeout(() => {
        showContent.value = true
      }, 500)

      const [apiBanners, apiProducts] = await Promise.all([
        fetchPublishedBanners(),
        fetchPublishedProducts()
      ])
      const usableBanners = (apiBanners || []).filter(item => {
        const url = item.imageUrl || item.image || ''
        if (!url) return false
        // OSS 当前桶是私有的（403），不能用来替换本地静态轮播图
        if (url.includes('aliyuncs.com')) return false
        return true
      })
      if (usableBanners.length) {
        carouselItems.value = usableBanners.map(item => ({
          id: item.id,
          image: item.imageUrl || item.image,
          title: item.title,
          description: item.subtitle || item.title
        }))
      }
      if (apiProducts.length) {
        products.value = mergeProducts(fallbackProducts, apiProducts)
      }
      if (products.value.length <= initialDisplayCount.value) {
        showAllProducts.value = true
      }
    })

    onUnmounted(() => {
      window.removeEventListener('scroll', handleScroll)
    })

    return {
      scrollY,
      isVisible,
      isStatsVisible,
      heroSection,
      statsSection,
      carouselItems,
      products,
      advantages,
      stats,
      scrollToProducts,
      navigateToProduct,
      showContent,
      companyName,
      isMobile,
      showAllProducts,
      toggleProducts,
      initialDisplayCount,
      displayedProducts,
      needsToggleButton
    }
  }
}
</script>

<style scoped>
.home-container {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
}

.particles-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
}

/* 英雄区样式 */
.hero-section {
  position: relative;
  height: 100vh;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-content {
  position: relative;
  z-index: 2;
  text-align: center;
  color: white;
  padding: 0 20px;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  transform: translateY(-10%);
}

.title-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 30px;
  margin-bottom: 40px;
  position: relative;
}

.title-line {
  width: 60px;
  height: 3px;
  background: rgba(255, 255, 255, 0.8);
  transform: scaleX(0);
  animation: lineExpand 1.2s ease forwards;
}

.hero-content h1 {
  font-size: 72px;
  margin: 0;
  font-weight: 600;
  letter-spacing: 8px;
  position: relative;
  display: flex;
  justify-content: center;
}

.letter-animation {
  display: inline-block;
  opacity: 0;
  transform: translateY(40px);
  animation: letterFadeIn 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.hero-content p {
  font-size: 28px;
  margin: 0 0 50px;
  opacity: 0;
  letter-spacing: 4px;
  animation: fadeIn 1s ease 1s forwards;
}

@keyframes lineExpand {
  0% {
    transform: scaleX(0);
    opacity: 0;
  }
  100% {
    transform: scaleX(1);
    opacity: 1;
  }
}

@keyframes letterFadeIn {
  0% {
    opacity: 0;
    transform: translateY(40px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  to {
    opacity: 0.9;
  }
}

/* 响应式调整 */
@media (max-width: 768px) {
  .hero-content {
    transform: translateY(0);
  }

  .hero-content h1 {
    font-size: 48px;
    letter-spacing: 4px;
  }

  .hero-content p {
    font-size: 20px;
    letter-spacing: 2px;
  }

  .title-line {
    width: 40px;
  }

  .title-wrapper {
    gap: 20px;
    margin-bottom: 30px;
  }

  .carousel-section {
    padding: 30px 0;
  }
  
  :deep(.el-carousel__item) {
    width: 60%;
    transform: translateX(15%) scale(0.83);
    z-index: 1;
  }

  :deep(.el-carousel__item.is-active) {
    transform: translateX(0) scale(1);
    width: 60%;
    z-index: 2;
  }

  .carousel-text {
    padding: 20px;
  }

  .carousel-text h2 {
    font-size: 18px;
    margin-bottom: 5px;
  }

  .carousel-text p {
    font-size: 14px;
  }

  .stat-number {
    font-size: 36px;
  }

  .stat-label {
    font-size: 16px;
  }
}

/* 添加按钮样式 */
.hero-content :deep(.el-button) {
  height: 50px;
  padding: 0 40px;
  font-size: 18px;
  border-radius: 25px;
  font-weight: 500;
  letter-spacing: 2px;
  background: transparent;
  border: 2px solid rgba(255, 255, 255, 0.9);
  transition: all 0.3s ease;
}

.hero-content :deep(.el-button:hover) {
  background: rgba(255, 255, 255, 0.9);
  color: #1890ff;
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
}

/* 轮播图样式 */
.carousel-section {
  padding: 60px 0;
  background: #f5f7fa;
}

.carousel-content {
  height: 100%;
  background-size: cover;
  background-position: center;
  border-radius: 8px;
  position: relative;
}

.carousel-text {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 30px;
  background: linear-gradient(to top, rgba(0,0,0,0.8), transparent);
  color: white;
}

/* 产品展示区样式 - 恢复网格 */
.products-section {
  padding: 80px 0;
  background: white;
}

.section-title {
  text-align: center;
  margin-bottom: 60px;
}

.section-title h2 {
  font-size: 36px;
  color: #2c3e50;
  margin-bottom: 20px;
}

.title-line {
  width: 60px;
  height: 3px;
  background: #1890ff;
  margin: 0 auto;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); /* 响应式网格 */
  gap: 30px;
  padding: 0 40px;
  max-width: 1400px;
  margin: 0 auto;
  transition: all 0.5s ease;
}

/* 收起状态的产品网格 */
.products-grid.collapsed {
  max-height: 800px; /* 限制高度，大约显示2行产品 */
  overflow: hidden;
  position: relative;
  transition: max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.products-grid.collapsed::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 120px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.9) 50%, rgba(255, 255, 255, 1) 100%);
  pointer-events: none;
  transition: opacity 0.3s ease;
}

.products-grid.collapsed::before {
  content: '';
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  width: 40px;
  height: 4px;
  background: #f0f0f0;
  border-radius: 2px;
  opacity: 0.6;
  z-index: 1;
}

/* 展开状态的产品网格 */
.products-grid:not(.collapsed) {
  max-height: none;
  overflow: visible;
}

.products-grid:not(.collapsed)::after {
  opacity: 0;
}

/* 收起展开按钮样式 */
.products-toggle {
  text-align: center;
  margin-top: 50px;
  padding: 0 40px;
}

/* 产品提示样式 */
.products-hint {
  margin-bottom: 25px;
  color: #8c8c8c;
}

.products-hint p {
  color: #8c8c8c;
  font-size: 15px;
  margin: 0;
  font-weight: 400;
  letter-spacing: 0.5px;
}

.toggle-btn {
  background: transparent;
  border: 2px solid #e8e8e8;
  color: #595959;
  padding: 0 32px;
  font-size: 15px;
  font-weight: 500;
  border-radius: 6px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  letter-spacing: 0.5px;
  height: 48px;
  line-height: 1;
}

/* 确保Element Plus按钮内容居中 */
.toggle-btn :deep(.el-button__content) {
  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
}

.toggle-btn :deep(.el-button__text) {
  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
}

/* 强制覆盖Element Plus的默认样式 */
.toggle-btn :deep(.el-button) {
  display: grid;
  place-items: center;
  height: 100%;
}

.toggle-btn:hover {
  border-color: #1890ff;
  color: #1890ff;
  background: rgba(24, 144, 255, 0.04);
  transform: translateY(-1px);
}

.toggle-btn:active {
  transform: translateY(0);
}

.toggle-btn .el-icon {
  font-size: 16px;
  transition: transform 0.3s ease;
  flex-shrink: 0;
}

.toggle-btn:hover .el-icon {
  transform: translateY(1px);
}

/* 图标位置调整 */
.icon-up, .icon-down {
  margin-right: 8px;
}

/* 产品卡片样式 */
.product-card {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  display: flex; /* 确保 flex 布局用于内部元素 */
  flex-direction: column;
}

.product-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 8px 24px rgba(0,0,0,0.15);
}

.product-image {
  height: 200px; /* 调整回适合网格的高度 */
  overflow: hidden;
  flex-shrink: 0;
}

.product-image img {
  width: 100%;
  height: 100%;
  object-fit: contain; /* 保持 contain */
  background: #f8f9fa;
  transition: transform 0.3s ease;
}

.product-card:hover .product-image img {
  transform: scale(1.05);
}

.product-info {
  padding: 20px; /* 调整回适合网格的内边距 */
  flex-grow: 1;
}

.product-info h3 {
  font-size: 18px;
  color: #2c3e50;
  margin-bottom: 10px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.product-info p {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  height: 44px; /* 限制高度约2-3行 */
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

/* 移除产品轮播相关样式 */
/* 
.product-carousel-wrapper { ... }
.product-carousel { ... }
.product-carousel-item { ... }
.product-carousel-item .product-card { ... }
.product-carousel-item .product-image { ... }
.product-carousel-item .product-image img { ... }
.product-carousel-item .product-info { ... }
.product-carousel-item .product-info h3 { ... }
.product-carousel-item .product-info p { ... }
:deep(.el-carousel__indicators--outside) { ... }
*/

/* 响应式调整 - 恢复网格布局的调整 */
@media (max-width: 768px) {
  .products-section {
    padding: 40px 0;
  }
  .products-grid {
    padding: 0 20px; /* 移动端调整内边距 */
    gap: 20px;
     grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); /* 移动端调整最小宽度 */
  }
  
  /* 移动端收起状态调整 */
  .products-grid.collapsed {
    max-height: 600px; /* 移动端限制高度，大约显示2行产品 */
  }
  
  .products-grid.collapsed::after {
    height: 100px; /* 移动端渐变遮罩高度调整 */
  }
  
  .products-grid.collapsed::before {
    bottom: 15px;
    width: 32px;
    height: 3px;
  }
  
   .product-image {
    height: 180px;
  }
  .product-info {
    padding: 15px;
  }
  .product-info h3 {
    font-size: 16px;
  }
  .product-info p {
    font-size: 13px;
     height: 39px; /* 移动端限制高度 */
     -webkit-line-clamp: 2;
  }
  .section-title h2 {
    font-size: 28px;
  }
  
  /* 移动端按钮样式调整 */
  .products-toggle {
    margin-top: 40px;
    padding: 0 20px;
  }
  
  .toggle-btn {
    padding: 0 24px;
    font-size: 14px;
    width: 100%; /* 移动端按钮全宽 */
    max-width: 280px;
    border-radius: 6px;
    height: 44px;
  }
  
  .products-hint p {
    font-size: 14px;
  }

  .carousel-section {
    padding: 30px 0;
  }
  
  :deep(.el-carousel__item) {
    width: 60%;
    transform: translateX(15%) scale(0.83);
    z-index: 1;
  }

  :deep(.el-carousel__item.is-active) {
    transform: translateX(0) scale(1);
    width: 60%;
    z-index: 2;
  }

  .carousel-text {
    padding: 20px;
  }

  .carousel-text h2 {
    font-size: 18px;
    margin-bottom: 5px;
  }

  .carousel-text p {
    font-size: 14px;
  }

  .stat-number {
    font-size: 36px;
  }

  .stat-label {
    font-size: 16px;
  }
}

/* 公司优势样式 */
.advantages-section {
  padding: 80px 0;
  background: #f5f7fa;
}

.advantages-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
  padding: 0 40px;
  max-width: 1400px;
  margin: 0 auto;
}

.advantage-card {
  text-align: center;
  padding: 40px 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
}

.advantage-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 24px rgba(0,0,0,0.15);
}

/* 数据统计样式 */
.stats-section {
  padding: 100px 0;
  background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
  color: white;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 40px;
  padding: 0 40px;
  max-width: 1200px;
  margin: 0 auto;
}

.stat-card {
  text-align: center;
}

.stat-number {
  font-size: 48px;
  font-weight: 600;
  margin-bottom: 10px;
}

.stat-label {
  font-size: 18px;
  opacity: 0.9;
}
</style> 